import React, { useEffect, useState, createContext, useContext } from 'react';
// Static exchange rates (approximate) relative to USD
const EXCHANGE_RATES: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.53,
  INR: 83.5,
  BRL: 4.97,
  MXN: 17.15,
  JPY: 149.5,
  NZD: 1.63,
  CHF: 0.88,
  ZAR: 19.05,
  CNY: 7.19,
  SGD: 1.34,
  HKD: 7.82,
  SEK: 10.35,
  NOK: 10.55,
  DKK: 6.86
};
// Country code to Currency code mapping
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  GB: 'GBP',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  PT: 'EUR',
  GR: 'EUR',
  CA: 'CAD',
  AU: 'AUD',
  IN: 'INR',
  BR: 'BRL',
  MX: 'MXN',
  JP: 'JPY',
  NZ: 'NZD',
  CH: 'CHF',
  ZA: 'ZAR',
  CN: 'CNY',
  SG: 'SGD',
  HK: 'HKD',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK'
};
interface CurrencyContextType {
  currencyCode: string;
  exchangeRate: number;
  isLoading: boolean;
  formatPrice: (amountInUSD: number) => string;
  convertPrice: (amountInUSD: number) => number;
}
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [locale, setLocale] = useState('en-US');
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const countryCode = data.country_code;
        const detectedCurrency = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
        // Only update if we support this currency
        if (EXCHANGE_RATES[detectedCurrency]) {
          setCurrencyCode(detectedCurrency);
          setExchangeRate(EXCHANGE_RATES[detectedCurrency]);
          // Try to set a reasonable locale for formatting
          setLocale(navigator.language || 'en-US');
        }
      } catch (error) {
        console.error('Failed to detect currency:', error);
        // Fallback to USD is already set by default state
      } finally {
        setIsLoading(false);
      }
    };
    detectCurrency();
  }, []);
  const convertPrice = (amountInUSD: number): number => {
    return amountInUSD * exchangeRate;
  };
  const formatPrice = (amountInUSD: number): string => {
    const convertedAmount = convertPrice(amountInUSD);
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: currencyCode === 'JPY' ? 0 : 2,
        maximumFractionDigits: currencyCode === 'JPY' ? 0 : 2
      }).format(convertedAmount);
    } catch (e) {
      // Fallback formatting if Intl fails
      return `${currencyCode} ${convertedAmount.toFixed(2)}`;
    }
  };
  return <CurrencyContext.Provider value={{
    currencyCode,
    exchangeRate,
    isLoading,
    formatPrice,
    convertPrice
  }} data-id="element-267">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}